import React, { useEffect, useState } from 'react';
import {
  UserPlus, Undo2, Wrench, CheckCircle2, Ban, RotateCcw,
  Pencil, Loader2, MapPin,
} from 'lucide-react';
import { Button, Dialog, Avatar, cn } from '../../ui';
import SearchableSelect from './SearchableSelect';
import LocalidadSelect from './LocalidadSelect';
import { AyudaNota, NOTA_MAX } from './NotaDeHistorial';
import { camposDeUbicacion, textoSitio } from '../utils/ubicacion';
import { avisoUnaPorPersona, CAMPO_CONFIRMACION } from '../utils/unaPorPersona';

/**
 * AccionesActivo
 *
 * Barra de acciones contextual según el estado del activo + los
 * mini-formularios (Dialog del DS v2) de cada acción.
 *
 * Transiciones:
 *   disponible → [Asignar] [Reparación] [Baja]
 *   asignado   → [Devolver] [Reparación] [Baja]
 *   reparacion → [Marcar reparado] [Baja]
 *   baja       → [Reactivar]
 *   siempre    → [Editar campos]
 *   con provincia y localidad en el tipo, salvo de baja → [Registrar movimiento] (Fase 3)
 *
 * Props:
 *   activo: objeto del activo (tiene .estado)
 *   schema: fields_schema del tipo (para el form de edición)
 *   usuarios: [{id,nombre}] para "Asignar a"
 *   busy: boolean (acción en curso)
 *   onAccion: (tipo, payload) => Promise
 */

// YYYY-MM-DD del día actual; lo usamos como valor inicial del input
// de fecha en cada modal. El backend exige fecha en cualquier evento
// y acepta pasada o futura — el usuario puede ajustarla si lo necesita.
function hoyISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Input de fecha reutilizable. Estilo alineado al 6b-2 cosmético
// (label uppercase + tracking) para que case con los demás campos
// de los modales de AccionesActivo.
function FechaInput({ value, onChange, label = 'Fecha', max }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
        {label}
      </label>
      <input
        type="date"
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
      />
    </div>
  );
}

export default function AccionesActivo({
  activo,
  schema = [],
  usuarios = [],
  empresas = [],
  negocios = [],
  oficinas = [],
  busy,
  onAccion,
  tieneCoste = false,
}) {
  const [modal, setModal] = useState(null); // 'asignar'|'devolver'|...|'editar'|null
  const [usuarioId, setUsuarioId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [cambios, setCambios] = useState({});
  const [localErr, setLocalErr] = useState(null);
  // Una por persona (01/11): la pregunta del modo «avisar». Mientras está, el botón principal es
  // «Asignar igualmente» y reenvía con la confirmación. Cambiar la persona o los campos la quita:
  // la confirmación vale para lo que se preguntó, no para otra cosa.
  const [avisoUPP, setAvisoUPP] = useState(null);
  // Registrar movimiento (Fase 3)
  const [movProvincia, setMovProvincia] = useState('');
  const [movLocalidad, setMovLocalidad] = useState('');
  const [movNota, setMovNota] = useState('');
  const ubic = camposDeUbicacion(schema);

  const estado = activo?.estado;
  useEffect(() => { setAvisoUPP(null); }, [usuarioId, cambios, modal]);

  function abrir(tipo) {
    setLocalErr(null);
    setUsuarioId('');
    setMotivo('');
    setFecha(hoyISO()); // siempre arranca en hoy, el usuario lo cambia si quiere
    setMovProvincia(''); setMovLocalidad(''); setMovNota('');
    if (tipo === 'editar') {
      // Precargar valores actuales de los campos del schema
      const init = {};
      schema.forEach((f) => {
        init[f.name] = activo[f.name] ?? '';
      });
      // Coste va aparte (columna fija). Solo se precarga si el tipo
      // está marcado como con coste; si no, se ignora silenciosamente.
      if (tieneCoste) {
        init.coste = activo.coste ?? '';
      }
      setCambios(init);
    }
    setModal(tipo);
  }

  async function confirmar(confirmarUPP = false) {
    setLocalErr(null);
    const upp = confirmarUPP ? { [CAMPO_CONFIRMACION]: true } : {};
    try {
      if (modal === 'asignar') {
        if (!usuarioId) throw new Error('Selecciona un usuario');
        await onAccion('asignar', { usuario_id: Number(usuarioId), motivo: motivo || null, fecha, ...upp });
      } else if (modal === 'baja') {
        if (!motivo.trim()) throw new Error('El motivo de baja es obligatorio');
        await onAccion('baja', { motivo: motivo.trim(), fecha });
      } else if (modal === 'editar') {
        // Editar es PUT al activo y no lleva fecha. NO crea evento... salvo
        // si cambia la persona: entonces el servidor registra la devolución
        // de la anterior y la asignación a la nueva, con fecha de hoy
        // (assetService.update, desde el 15/10). Sin eso el activo parecía
        // asignado sin estarlo, y la baja de esa persona no lo veía.
        // Normalizamos `coste` antes del PUT: '' / null → null (NULL en
        // BBDD); valor → Number. El backend revalida ≥ 0 / numérico.
        const cambiosOut = { ...cambios };
        if (tieneCoste) {
          const c = cambiosOut.coste;
          cambiosOut.coste = (c === '' || c == null) ? null : Number(c);
        }
        await onAccion('editar', { cambios: cambiosOut, edit_reason: motivo || 'Edición de campos', ...upp });
      } else if (modal === 'devolver') {
        await onAccion('devolver', { motivo: motivo || null, fecha });
      } else if (modal === 'reparacion_inicio') {
        await onAccion('reparacion_inicio', { motivo: motivo || null, fecha });
      } else if (modal === 'reparacion_fin') {
        await onAccion('reparacion_fin', { motivo: motivo || null, fecha });
      } else if (modal === 'reactivar') {
        await onAccion('reactivar', { motivo: motivo || null, fecha });
      } else if (modal === 'movimiento') {
        // El servidor lo vuelve a comprobar todo (provincia de la lista, municipio del catálogo del INE
        // de esa provincia, fecha ≤ hoy en Madrid).
        if (!movProvincia) throw new Error('Elige la provincia');
        if (!movLocalidad.trim()) throw new Error('Elige la localidad');
        if (fecha > hoyISO()) throw new Error('La fecha no puede ser futura');
        await onAccion('movimiento', {
          ubicacion_provincia: movProvincia, ubicacion_localidad: movLocalidad.trim(), fecha,
          notas: movNota.trim() || null,
        });
      }
      setModal(null);
    } catch (e) {
      // «Avisar» (01/11): se pregunta aquí mismo y el botón reenvía con la confirmación. «Bloquear»
      // no se puede confirmar: cae en el error de abajo, con el mensaje del servidor.
      const aviso = avisoUnaPorPersona(e);
      if (aviso && aviso.confirmable && (modal === 'asignar' || modal === 'editar')) {
        setAvisoUPP(aviso.pregunta);
        return;
      }
      // El MOTIVO lo manda el servidor en `error` (handleServiceError). `e.message` a secas es el de
      // axios —«Request failed with status code 400»— y no dice nada a quien lo lee: así salió el
      // fallo de asignar a Paula Crespo (15/10), con el porqué en la respuesta y sin enseñarlo.
      setLocalErr(e?.response?.data?.error || e?.response?.data?.message || e.message
        || 'No se pudo completar la acción');
    }
  }

  // Botones según estado
  const botones = [];
  if (estado === 'disponible') {
    botones.push(['asignar', UserPlus, 'Asignar', 'primary']);
    botones.push(['reparacion_inicio', Wrench, 'Reparación', 'secondary']);
    botones.push(['baja', Ban, 'Baja', 'secondary']);
  } else if (estado === 'asignado') {
    botones.push(['devolver', Undo2, 'Devolver', 'primary']);
    botones.push(['reparacion_inicio', Wrench, 'Reparación', 'secondary']);
    botones.push(['baja', Ban, 'Baja', 'secondary']);
  } else if (estado === 'reparacion') {
    botones.push(['reparacion_fin', CheckCircle2, 'Marcar reparado', 'primary']);
    botones.push(['baja', Ban, 'Baja', 'secondary']);
  } else if (estado === 'baja') {
    botones.push(['reactivar', RotateCcw, 'Reactivar', 'primary']);
  }
  // La ubicación va por su cuenta del estado: se puede mover un coche asignado, libre o en reparación.
  // De baja no: el servidor responde 409 (movimiento_no_valido), así que ni se ofrece.
  if (ubic && estado !== 'baja') botones.push(['movimiento', MapPin, 'Registrar movimiento', 'secondary']);

  const TITULOS = {
    asignar: 'Asignar activo',
    devolver: 'Devolver activo',
    reparacion_inicio: 'Enviar a reparación',
    reparacion_fin: 'Marcar como reparado',
    baja: 'Dar de baja',
    reactivar: 'Reactivar activo',
    editar: 'Editar campos',
    movimiento: 'Registrar movimiento',
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {botones.map(([tipo, Icon, label, variant]) => (
          <Button
            key={tipo}
            variant={variant === 'primary' ? undefined : 'secondary'}
            onClick={() => abrir(tipo)}
            disabled={busy}
          >
            <Icon className="w-4 h-4 mr-1" />
            {label}
          </Button>
        ))}
        <Button variant="secondary" onClick={() => abrir('editar')} disabled={busy}>
          <Pencil className="w-4 h-4 mr-1" />
          Editar
        </Button>
      </div>

      <Dialog
        open={!!modal}
        onClose={() => !busy && setModal(null)}
        title={modal ? TITULOS[modal] : ''}
        description={
          modal && activo ? `${activo.referencia}` : ''
        }
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => !busy && setModal(null)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              variant={modal === 'baja' ? 'danger' : undefined}
              onClick={() => confirmar(!!avisoUPP)}
              disabled={busy}
            >
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {avisoUPP
                ? 'Asignar igualmente'
                : modal === 'asignar'
                ? 'Asignar'
                : modal === 'baja'
                ? 'Dar de baja'
                : 'Confirmar'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          {modal === 'asignar' && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                  Asignar a
                </label>
                <SearchableSelect
                  value={usuarioId}
                  onChange={setUsuarioId}
                  options={usuarios}
                  placeholder="Selecciona…"
                />
              </div>
              {(() => {
                const u = usuarios.find(
                  (x) => String(x.id) === String(usuarioId)
                );
                if (!u) return null;
                return (
                  <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <Avatar name={u.nombre} size="sm" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {u.nombre}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        Pasará a ser el responsable del activo.
                      </div>
                    </div>
                  </div>
                );
              })()}
              <FechaInput value={fecha} onChange={setFecha} label="Fecha de la asignación" />
            </div>
          )}

          {modal === 'baja' && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                  Motivo de la baja <span className="text-rose-500 normal-case">*</span>
                </label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={3}
                  placeholder="Ej: siniestro total / fin de vida útil / robo…"
                  className="w-full border border-slate-200 rounded-md px-3 py-2"
                />
              </div>
              <FechaInput value={fecha} onChange={setFecha} label="Fecha de la baja" />
            </div>
          )}

          {modal === 'editar' && (
            <div className="space-y-3">
              {schema.length === 0 && (
                <p className="text-xs text-slate-500">
                  Este tipo no tiene campos editables.
                </p>
              )}
              {schema.map((f) => (
                <div key={f.name}>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                    {f.label || f.name}
                    {f.required && <span className="text-rose-500 normal-case"> *</span>}
                  </label>
                  {ubic && f.name === ubic.localidad.name ? (
                    // Localidad del catálogo del INE de la provincia elegida (Fase 3b).
                    <LocalidadSelect
                      provincia={cambios[ubic.provincia.name] || ''}
                      value={cambios[f.name] ?? ''}
                      onChange={(v) => setCambios((c) => ({ ...c, [f.name]: v }))}
                    />
                  ) : f.type === 'select' ? (
                    <select
                      value={cambios[f.name] ?? ''}
                      onChange={(e) =>
                        setCambios((c) => ({
                          ...c,
                          [f.name]: e.target.value,
                          // Otra provincia: la localidad de la anterior ya no vale.
                          ...(ubic && f.name === ubic.provincia.name && e.target.value !== c[f.name]
                            ? { [ubic.localidad.name]: '' } : {}),
                        }))
                      }
                      className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
                    >
                      <option value="">—</option>
                      {(f.options || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.type === 'boolean' ? (
                    <select
                      value={String(cambios[f.name] ?? '')}
                      onChange={(e) =>
                        setCambios((c) => ({ ...c, [f.name]: e.target.value === 'true' }))
                      }
                      className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
                    >
                      <option value="">—</option>
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  ) : f.type === 'user_link' ? (
                    // SearchableSelect con la lista de personas que ya
                    // viene como prop. Almacenamos el ID como Number
                    // (matching del backend) o null si se limpia.
                    <SearchableSelect
                      value={cambios[f.name] ?? ''}
                      onChange={(v) =>
                        setCambios((c) => ({
                          ...c,
                          [f.name]: v === '' || v == null ? null : Number(v),
                        }))
                      }
                      options={usuarios}
                      placeholder="Selecciona un usuario…"
                    />
                  ) : f.type === 'company_link' || f.type === 'empresa_link' ? (
                    // Nota: el backend canonicaliza empresa_link → company_link
                    // al guardar (schemaValidator), así que en GETs el field
                    // suele venir como company_link. Aceptamos ambos por
                    // compatibilidad con schemas creados antes del fix v2.
                    <SearchableSelect
                      value={cambios[f.name] ?? ''}
                      onChange={(v) =>
                        setCambios((c) => ({
                          ...c,
                          [f.name]: v === '' || v == null ? null : Number(v),
                        }))
                      }
                      options={empresas}
                      placeholder="Selecciona una empresa…"
                    />
                  ) : f.type === 'business_link' ? (
                    <SearchableSelect
                      value={cambios[f.name] ?? ''}
                      onChange={(v) =>
                        setCambios((c) => ({
                          ...c,
                          [f.name]: v === '' || v == null ? null : Number(v),
                        }))
                      }
                      options={negocios}
                      placeholder="Selecciona un negocio…"
                    />
                  ) : f.type === 'office_link' ? (
                    <SearchableSelect
                      value={cambios[f.name] ?? ''}
                      onChange={(v) =>
                        setCambios((c) => ({
                          ...c,
                          [f.name]: v === '' || v == null ? null : Number(v),
                        }))
                      }
                      options={oficinas}
                      placeholder="Selecciona una oficina…"
                    />
                  ) : (
                    <input
                      value={cambios[f.name] ?? ''}
                      onChange={(e) =>
                        setCambios((c) => ({ ...c, [f.name]: e.target.value }))
                      }
                      type={f.type === 'number' || f.type === 'decimal' ? 'number' : 'text'}
                      className="w-full border border-slate-200 rounded-md px-3 py-2"
                    />
                  )}
                  {f.type === 'user_link' && (
                    <p className="mt-1 text-xs text-slate-500">
                      Si cambias la persona, se registra la devolución de la anterior y la
                      asignación a la nueva, con fecha de hoy. Para otra fecha, usa Devolver y Asignar.
                    </p>
                  )}
                </div>
              ))}

              {/* Campo fijo `coste` — solo si el tipo lo lleva. Fuera
                  del bucle del schema porque NO es un campo dinámico. */}
              {tieneCoste && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                    Coste
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={cambios.coste ?? ''}
                      onChange={(e) =>
                        setCambios((c) => ({ ...c, coste: e.target.value }))
                      }
                      placeholder="0,00"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      €
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Acciones de confirmación simple: campo motivo opcional + fecha */}
          {['devolver', 'reparacion_inicio', 'reparacion_fin', 'reactivar'].includes(modal) && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                  Comentario (opcional)
                </label>
                <input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2"
                  placeholder="Nota para el historial…"
                />
              </div>
              <FechaInput
                value={fecha}
                onChange={setFecha}
                label={
                  modal === 'devolver'
                    ? 'Fecha de la devolución'
                    : modal === 'reparacion_inicio'
                    ? 'Fecha de entrada en reparación'
                    : modal === 'reparacion_fin'
                    ? 'Fecha de finalización'
                    : 'Fecha de la reactivación'
                }
              />
            </div>
          )}

          {modal === 'movimiento' && ubic && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Ubicación actual: {textoSitio(activo?.provincia, activo?.localidad) || 'sin registrar'}
              </p>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">Provincia *</label>
                <select
                  value={movProvincia}
                  onChange={(e) => { setMovProvincia(e.target.value); setMovLocalidad(''); }}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
                >
                  <option value="">Elige la provincia…</option>
                  {ubic.provincia.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">Localidad *</label>
                <LocalidadSelect provincia={movProvincia} value={movLocalidad} onChange={setMovLocalidad} />
              </div>
              <FechaInput value={fecha} onChange={setFecha} label="Fecha del movimiento" max={hoyISO()} />
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">Nota (opcional)</label>
                <textarea
                  value={movNota}
                  rows={3}
                  maxLength={NOTA_MAX}
                  onChange={(e) => setMovNota(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 resize-y"
                />
                <p className="mt-1 text-[11px] text-slate-400">{movNota.length.toLocaleString('es-ES')} / {NOTA_MAX.toLocaleString('es-ES')}</p>
                <AyudaNota />
              </div>
            </div>
          )}

          {avisoUPP && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              {avisoUPP}
            </p>
          )}
          {localErr && (
            <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">
              {localErr}
            </p>
          )}
        </div>
      </Dialog>
    </>
  );
}
